import type { JsonSchema, UISchemaElement } from "@jsonforms/core";
import { InputHTMLAttributes } from "react";
import { ValidationRule } from "react-hook-form";

export interface DropdownOption<T = string> {
  value: T;
  label: string;
  disabled?: boolean;
  indent?: number;
}

type ServerProps<Type> = {
  asyncProps?: Array<
    keyof Omit<Extract<FormType, { type: Type }>, "serverProps">
  >;
  dependsOnFieldsWithNames?: Array<string>;
};

export type Input = {
  name: string;
  type: "text" | "email" | "number" | "password";
  label: string;
  required: boolean;
  description?: string;
  descriptionType?: "info" | "warning" | "error";
  editable?: boolean;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "pattern" | "size">;
  pattern?: ValidationRule<RegExp>;
  value?: string;
  error?: string;
} & ServerProps<"text">;

export type SettingsResult = {
  name: string;
  type: "settings-result";
  label: string;
  value: string;
} & ServerProps<"text">;

export type Hidden = {
  name: string;
  type: "hidden";
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "pattern" | "size">;
  value?: string;
} & ServerProps<"hidden">;

type Dropdown = {
  name: string;
  type: "dropdown";
  placeholder?: string;
  description?: string;
  isMulti?: boolean;
  appliedMultiSelect?: boolean;
  creatable?: boolean;
  label: string;
  required: boolean;
  value?: DropdownOption;
  dropdownOptions: DropdownOption[];
  disabled?: boolean;
  disableCheckmark?: boolean;
  hideSelectedOptions?: boolean;
} & ServerProps<"dropdown">;

export interface ChoiceOption<T = string> extends DropdownOption<T> {
  description?: string;
  recommended?: boolean;
  /** Shown below the choice while this option is selected. */
  fields?: FormType[];
}

export type Choice = {
  name: string;
  type: "choice";
  description?: string;
  label: string;
  required: boolean;
  value?: ChoiceOption;
  options: ChoiceOption[];
  disabled?: boolean;
} & ServerProps<"choice">;

export type Information = {
  name: string;
  type: "information";
  title: string;
  description: string;
  link?: {
    label: string;
    url: string;
  };
} & ServerProps<"information">;

type Checkbox = {
  name: string;
  type: "checkbox";
  label: string;
  required: boolean;
  value?: boolean;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "pattern">;
  description?: string;
} & ServerProps<"checkbox">;

type CopyInput = {
  type: "copy";
  value: string;
  editable?: boolean;
  required: boolean;
  name: string;
  label: string;
  description?: string;
} & ServerProps<"copy">;

type BigQuery = {
  name: string;
  type: "big-query-form";
  label: string;
  required: boolean;
  value?: string;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "pattern">;
} & ServerProps<"big-query-form">;

export type SubForm = {
  type: "sub_form";
  name: string;
  editable?: boolean;
  required: false;
  label?: string;
  fields: FormType[];
  defaultValues?: object;
  value?: boolean;
  error?: string;
} & ServerProps<"sub_form">;

export type Repeater = {
  type: "repeater";
  name: string;
  label: string;
  singularLabel: string;
  template: SubForm;
  editable: boolean;
  min: number;
  max: number;
  value: SubForm[];
  error: string;
};

type OAuthForm = {
  type: "oauth";
  name?: string;
  label: string;
  fields: FormType[];
  slug?: string;
  defaultValues?: object;
};

type GoogleAnalytics4Form = {
  type: "google-analytics-4-form";
  name: "google-analytics-4-form";
  accounts: {
    displayName: string;
    account: string;
    propertySummaries: {
      displayName: string;
      property: string;
      propertyType: string;
    }[];
  }[];
};

type GoogleAnalyticsForm = {
  type: "google-analytics-form";
  name: "google-analytics-form";
  accounts: {
    name: string;
    id: string;
    webProperties: {
      name: string;
      id: string;
      profiles: {
        name: string;
        id: string;
      }[];
    }[];
  }[];
};

type FacebookAdsForm = {
  type: "facebook-ads-form";
  name: "facebook-ads-form";
  accounts: {
    label: string;
    value: string;
  }[];
};

type GoogleAdsForm = {
  type: "google-ads-form";
  name: "google-ads-form";
  accounts: DropdownOption[];
};

type RedditAdsForm = {
  type: "reddit-ads-form";
  name: "reddit-ads-form";
  selectedAdAccounts?: DropdownOption[];
  breakdowns: Array<DropdownOption | string>;
  fields: Array<DropdownOption | string>;
  timezones: Array<DropdownOption | string>;
};

type KlaviyoForm = {
  type: "klaviyo-form";
  name: "klaviyo-form";
  fields?: FormType[];
};

type GorgiasForm = {
  type: "gorgias-form";
  name: "gorgias-form";
  fields?: FormType[];
};

type ShopifyCustomReportsForm = {
  type: "shopify-custom-reports";
  name: "shopify-custom-reports";
};

export type GoogleDriveForm = {
  type: "google-drive-form";
  name: "google-drive-form";
  tableName: Input;
  fileType: DropdownOption[];
};

type OpenWeatherForm = {
  type: "openweather-form";
  name: "openweather-form";
};

type AppStoreConnect = {
  name: string;
  type: "app-store-connect-form";
  label: string;
  required: boolean;
  value?: string;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "pattern">;
} & ServerProps<"app-store-connect-form">;

type SnowflakeForm = {
  name: string;
  type: "snowflake-form";
  label: string;
  required: boolean;
  value?: string;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "pattern">;
} & ServerProps<"snowflake-form">;

type FileUpload = {
  name: string;
  type: "file-upload";
  label: string;
  required: boolean;
  value?: string;
  description?: string;
  validityCheckString?: string;
  extensions?: string[];
} & ServerProps<"file-upload">;

export type JsonForm = {
  name: string;
  type: "json-form";
  label?: string;
  required: boolean;
  schema: JsonSchema;
  uischema?: UISchemaElement;
  data?: Record<string, unknown>;
} & ServerProps<"json-form">;

export type FormType =
  | SubForm
  | Repeater
  | OAuthForm
  | Input
  | Hidden
  | Dropdown
  | Choice
  | Information
  | Checkbox
  | CopyInput
  | BigQuery
  | GoogleAnalyticsForm
  | GoogleAnalytics4Form
  | FacebookAdsForm
  | SettingsResult
  | AppStoreConnect
  | GoogleAdsForm
  | RedditAdsForm
  | GoogleDriveForm
  | OpenWeatherForm
  | KlaviyoForm
  | GorgiasForm
  | ShopifyCustomReportsForm
  | SnowflakeForm
  | FileUpload
  | JsonForm;

const isSubForm = (form: FormType): form is SubForm => form.type === "sub_form";

export const hasError = (form: FormType) => isSubForm(form) && !!form.error;
